import { concat, constant, filter, isEqual, negate, set, size, slice, times } from 'lodash/fp';
import { keyBy } from '@mews/fp';
import { createReducer, DateTimeUtcService, DateTimeUtils } from '@mews/framework';
import { FLEXIBLE_RATES_FILTER_CHANGED } from '../../middlewares/gtmActions';
import { SERVICE_DATA_LOADED } from '../entities/services/actionTypes';
import { BOOKING_RESTARTED } from '../global/actionTypes';
import {
    AGE_CATEGORY_OCCUPANCY_SET,
    CLEAR_TIMES,
    DATES_SET,
    DEFAULT_RESOURCE_ORDER_SET,
    END_DATE_SET,
    END_TIME_UTC_SET,
    HOTEL_SELECTED,
    PRODUCT_ADDED,
    PRODUCT_REMOVED,
    PROMOTED_SERVICE_ADDED,
    PROMOTED_SERVICE_REMOVED,
    RESOURCE_ORDER_ADDED,
    ROOM_SELECTED,
    SPACE_TYPE_COUNT_RESET,
    SPACE_TYPE_COUNT_SET,
    START_DATE_SET,
    START_TIME_UTC_SET,
    VOUCHER_CODE_SET,
} from './actionTypes';
import {
    AgeCategoryOccupancySetPayload,
    DatesSetPayload,
    DefaultResourceOrderSetPayload,
    EndDateSetPayload,
    EndTimeUtcSetPayload,
    HotelSelectedPayload,
    ProductAddedPayload,
    ProductRemovedPayload,
    PromotedServiceAddedPayload,
    PromotedServiceRemovedPayload,
    RoomSelectedPayload,
    SpaceTypeCountSetPayload,
    StartDateSetPayload,
    StartTimeUtcSetPayload,
    VoucherCodeSetPayload,
} from './payloadTypes';
import { ResourceOrder } from './types';

// Be aware that we rewrite initialState in global.Mews.Distributor
// in /distributor/web-app/src/bootstrap/distributor.ts
const INITIAL_STATE = {};

export const resourceOrder = createReducer(INITIAL_STATE, {
    [HOTEL_SELECTED]: ({ roomId, ...state }: ResourceOrder, { hotelId }: HotelSelectedPayload) => ({
        ...state,
        hotelId,
    }),

    [DEFAULT_RESOURCE_ORDER_SET]: (
        state: ResourceOrder,
        { productIds }: DefaultResourceOrderSetPayload
    ): ResourceOrder => ({ ...state, productIds }),

    [SERVICE_DATA_LOADED]: (state: ResourceOrder) => ({ ...state, isVoucherDirty: false }),
    [FLEXIBLE_RATES_FILTER_CHANGED]: (
        state: ResourceOrder,
        { state: showFlexiblePrices }: { changedByUser: boolean; state: boolean }
    ) => ({
        ...state,
        showFlexiblePrices,
    }),

    [DATES_SET]: (state: ResourceOrder, { startDate, endDate }: DatesSetPayload) =>
        resetPromotedService(
            { ...state, startDate, endDate },
            state.startDate !== startDate || state.endDate !== endDate
        ),

    [START_DATE_SET]: (state: ResourceOrder, { startDate }: StartDateSetPayload) =>
        resetPromotedService({ ...state, startDate }, state.startDate !== startDate),

    [END_DATE_SET]: (state: ResourceOrder, { endDate }: EndDateSetPayload) =>
        resetPromotedService({ ...state, endDate }, state.endDate !== endDate),

    [START_TIME_UTC_SET]: (state: ResourceOrder, payload: StartTimeUtcSetPayload) => {
        const { timezone, startTimeUtc: startUtc } = payload;
        const startTimeUtc =
            startUtc != null ? DateTimeUtcService.toISOString(startUtc, { suppressMilliseconds: true }) : undefined;
        const startDate =
            startUtc != null
                ? DateTimeUtils.dateTimeUtcStringToWallClockDate(startUtc.value, timezone).value
                : state.startDate;
        // Update the startDate to ensure the two dates are in sync.
        // TODO: Use UTC time for all dates and times, not just hourly services.
        return resetPromotedService({ ...state, startTimeUtc, startDate }, state.startTimeUtc !== startTimeUtc);
    },

    [END_TIME_UTC_SET]: (state: ResourceOrder, payload: EndTimeUtcSetPayload) => {
        const { timezone, endTimeUtc: endUtc } = payload;
        const endTimeUtc =
            endUtc != null ? DateTimeUtcService.toISOString(endUtc, { suppressMilliseconds: true }) : undefined;
        const endDate =
            endUtc != null
                ? DateTimeUtils.dateTimeUtcStringToWallClockDate(endUtc.value, timezone).value
                : state.endDate;
        // Update the endDate to ensure the two dates are in sync.
        // TODO: Use UTC time for all dates and times, not just hourly services.
        return resetPromotedService({ ...state, endTimeUtc, endDate }, state.endTimeUtc !== endTimeUtc);
    },

    [CLEAR_TIMES]: (state: ResourceOrder) =>
        resetPromotedService({ ...state, startTimeUtc: undefined, endTimeUtc: undefined }, true),

    [VOUCHER_CODE_SET]: (state: ResourceOrder, { voucherCode }: VoucherCodeSetPayload) => ({
        ...state,
        voucherCode,
        isVoucherDirty: true,
    }),

    [ROOM_SELECTED]: (state: ResourceOrder, { roomId }: RoomSelectedPayload) => ({
        ...state,
        roomId,
    }),

    [SPACE_TYPE_COUNT_RESET]: (state: ResourceOrder) => ({ ...state, occupancies: [] }),

    [SPACE_TYPE_COUNT_SET]: (state: ResourceOrder, { count, occupancy }: SpaceTypeCountSetPayload) => {
        const { occupancies, promotedServices } = state;

        const newOccupancies = spaceTypeCountSetUpdater(count, { occupancyData: keyBy('ageCategoryId', occupancy) })(
            occupancies
        );

        return {
            ...state,
            promotedServices,
            occupancies: newOccupancies,
        };
    },

    [AGE_CATEGORY_OCCUPANCY_SET]: (
        state: ResourceOrder,
        { categoryIndex, ageCategoryId, personCount }: AgeCategoryOccupancySetPayload
    ) => set(['occupancies', categoryIndex, 'occupancyData', ageCategoryId], { ageCategoryId, personCount }, state),

    [PRODUCT_ADDED]: (state: ResourceOrder, { productId }: ProductAddedPayload) => ({
        ...state,
        productIds: [...state.productIds, productId],
    }),

    [PRODUCT_REMOVED]: (state: ResourceOrder, { productId }: ProductRemovedPayload) => ({
        ...state,
        productIds: filter<string[]>(negate(isEqual(productId)), state.productIds),
    }),

    [PROMOTED_SERVICE_ADDED]: (
        state: ResourceOrder,
        {
            promotedServiceId,
            count,
            startUtc,
            endUtc,
            timeUnitPeriod,
            serviceAvailableCount,
        }: PromotedServiceAddedPayload
    ) => {
        if (state.promotedServices.find((service) => service.id === promotedServiceId)) {
            return {
                ...state,
                promotedServices: state.promotedServices.map((service) =>
                    service.id === promotedServiceId
                        ? { ...service, count, startUtc, endUtc, timeUnitPeriod, serviceAvailableCount }
                        : service
                ),
            };
        }
        return {
            ...state,
            promotedServices: [
                ...state.promotedServices,
                { id: promotedServiceId, count, startUtc, endUtc, timeUnitPeriod, serviceAvailableCount },
            ],
        };
    },

    [PROMOTED_SERVICE_REMOVED]: (state: ResourceOrder, { promotedServiceId }: PromotedServiceRemovedPayload) => ({
        ...state,
        promotedServices: state.promotedServices.filter((service) => service.id !== promotedServiceId),
    }),

    [BOOKING_RESTARTED]: ({ roomId, promotedServices, startTimeUtc, endTimeUtc, ...state }: ResourceOrder) => ({
        ...state,
        promotedServices: [],
        startTimeUtc: undefined,
        endTimeUtc: undefined,
    }),
    [RESOURCE_ORDER_ADDED]: (state: ResourceOrder) => ({
        ...state,
        promotedServices: [],
    }),
});

const spaceTypeCountSetUpdater =
    <T>(count: number, defaultOccupancy: T) =>
    (occupancies: T[] = []) => {
        if (count < size(occupancies)) {
            return slice(0, count, occupancies);
        }

        return concat(occupancies, times(constant(defaultOccupancy), count - size(occupancies)));
    };

const resetPromotedService = (state: ResourceOrder, hasDateChanged: boolean): ResourceOrder =>
    state.promotedServices != null && hasDateChanged ? { ...state, promotedServices: [] } : state;
